import React from "react";
import { useTranslation } from "react-i18next";

import getArticleChannelData from "astrid-firestore/src/api/articles/getArticleChannelData";
import organizationTypes from "astrid-firestore/src/api/organizations/constants/organizationTypes";

import CopyIcon from "../../ui/components/CopyIcon/CopyIcon";
import { timestampColumn } from "../../ui/utils/columns";

import ArticleDeliveryStatus from "../components/ArticleDeliveryStatus/ArticleDeliveryStatus";
import ArticleItem from "../components/ArticleItem/ArticleItem";
import ArticlePublicationStatus from "../components/ArticlePublicationStatus/ArticlePublicationStatus";

import useArticleTypeTranslations from "./useArticleTypeTranslations";

function getChannelData(article, key) {
	return article.channelIds
		? [
				...new Set(
					article.channelIds
						.map((channelId) => getArticleChannelData(article, channelId, key))
						.filter(Boolean),
				),
		  ].join(", ")
		: null;
}

export default function useArticlesTableColumns({ organization, channels } = {}) {
	const { t } = useTranslation();

	const articleTypeTranslations = useArticleTypeTranslations();

	return [
		{
			id: "name",
			collapsing: true,
			Header: t("title", "Title"),
			Cell: ({ row }) => <ArticleItem article={row} />,
		},
		{
			id: "isbn",
			Header: t("isbn", "ISBN"),
			collapsing: true,
			Cell: ({ row }) => (
				<div>
					{row.isbn} {"  "}
					<CopyIcon value={row.isbn} />
				</div>
			),
		},
		{
			id: "type",
			Header: t("type", "Type"),
			collapsing: true,
			accessor: (article) => articleTypeTranslations[article.type],
		},
		{
			id: "publisher.name",
			Header: t("publisher", "Publisher"),
			collapsing: true,
			hidden: organization?.type === organizationTypes.PUBLISHER,
		},
		{
			id: "serie",
			Header: t("serie", "Serie"),
			collapsing: true,
			Cell: ({ row }) => {
				const { serie, serieNumber } = row;

				return (
					<>
						{serie && `${serie.name}`}
						{serieNumber && `, ${t("part", "Part")} ${serieNumber}`}
					</>
				);
			},
		},
		{
			id: "publicationStatus",
			collapsing: true,
			disableSortBy: true,
			Header: t("publicationStatus", "Publication status"),
			Cell: ({ row }) => <ArticlePublicationStatus article={row} />,
		},
		{
			id: "status",
			collapsing: true,
			disableSortBy: true,
			Header: t("distribution", "Distribution"),
			Cell: ({ row }) => <ArticleDeliveryStatus article={row} />,
		},
		{
			id: "channelIds",
			collapsing: true,
			disableSortBy: true,
			Header: t("channels", "Channels"),
			accessor: (article) => {
				const articleChannels = (channels || [])
					.filter((channel) => article.channelIds?.includes(channel.id))
					.map(({ name }) => name);

				return articleChannels.length > 1
					? `${articleChannels[0]} +${articleChannels.length - 1}`
					: articleChannels[0];
			},
		},
		{
			id: "announcementDate",
			collapsing: true,
			disableSortBy: true,
			Header: t("announcementDate", "Announcement date"),
			accessor: (article) => getChannelData(article, "announcementDate"),
		},
		{
			id: "releaseDate",
			collapsing: true,
			disableSortBy: true,
			Header: t("releaseDate", "Release date"),
			accessor: (article) => getChannelData(article, "releaseDate"),
		},
		{
			id: "takedownDate",
			collapsing: true,
			disableSortBy: true,
			Header: t("takedownDatea", "Takedown date"),
			accessor: (article) => getChannelData(article, "takedownDate"),
		},
		timestampColumn({ id: "updated", Header: t("updated", "Updated"), time: false }),
		timestampColumn({ id: "created", Header: t("created", "Created"), time: false }),
	];
}
